<template>
  <Teleport to="body">
    <Transition name="token-info">
      <div v-if="modelValue" class="fixed inset-0 z-[120] flex items-end justify-center" @click.self="close">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/40" @click="close" />

        <!-- Bottom sheet -->
        <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl px-6 pt-6 pb-sheet-8 z-10 flex flex-col" style="max-height: 85vh;">
          <!-- Close button -->
          <button @click="close" class="absolute top-4 right-4 p-1 z-10">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#41291A" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <h2 class="text-xl font-bold text-ink mb-4 pr-8">Как устроен лимит</h2>

          <div class="flex-1 overflow-y-auto -mx-6 px-6 text-14 leading-relaxed text-ink">

            <!-- Платным конкретный лимит не называем: у них он и так щедрый, а
                 названное число превращает разговор в спор о цифре. Бесплатным
                 наоборот показываем — это и объяснение, и повод подписаться. -->
            <p v-if="isPremium" class="mb-4">Лимит дневной и общий на все обращения к Зое. Вот на что тратятся токены:</p>
            <p v-else class="mb-4">Ваш дневной лимит — {{ fmtLimit }} токенов. Это условная цифра, вот на что тратятся токены:</p>

            <p class="mb-4"><b>1 распознавание еды: ~2.000-4.000 токенов</b><template v-if="!isPremium"> (если тратить лимит только на распознавания еды, хватит примерно на {{ recognitions }} в день)</template>.</p>

            <!-- Только подписчикам: у них распознавание при исчерпании и правда
                 продолжает работать (checkTokenLimitFreeOnly). Бесплатным этого
                 не обещаем — у них оно останавливается вместе со всем остальным. -->
            <p v-if="isPremium" class="mb-4 text-13" style="color: rgba(65,41,26,0.7);">*При этом даже если потрачены все токены, <i>все равно можно добавлять еду через распознавание фото, так как это критично важно для ведения дневника.</i></p>

            <p class="mb-4"><b>Отчеты Зои за день, неделю, месяц: ~3.000-20.000 токенов</b> (за день меньше, за месяц — больше). Отчет за неделю с максимальным количеством опций, понятно, больше затрат<template v-if="!isPremium"> — это около {{ weeklyPct }}% дневного лимита</template>.</p>

            <p class="mb-4"><b>1 сообщение + ответ Зои в чате: ~3.000-6.000 токенов</b> каждое сообщение+ответ Зои.</p>

            <p class="mb-4">Чтобы Зоя была в курсе всего, что вас касается вместе с каждым сообщением, даже если это просто «Привет!» мы отправляем большой пакет обезличенных данных (по закону мы не отправляем ваши личные данные: имя, email и прочее, что может вас идентифицировать):</p>

            <ul class="mb-4 list-disc pl-5 space-y-1">
              <li>все ваши параметры из анкеты, цели, изменения веса,</li>
              <li>историю сообщений в чате с Зоей,</li>
              <li>все ваши данные дневника. Детально: каждый прием пищи за неделю (время, состав, КБЖУ каждого блюда, КБЖУ, клетчатка, витамины и минералы за день), саммари за каждую неделю за последний месяц, саммари за каждый месяц за последний год).</li>
            </ul>

            <p class="mb-6">Не важно, отправляется ли сообщение типа «Привет» или «Сделай мне здоровый недельный рацион по моим вкусам» — расходы токенов будут сопоставимы.<template v-if="!isPremium"> Дневного лимита хватает примерно на {{ messages }} сообщений с ответом, если не тратить его больше ни на что.</template><template v-else> Дневного лимита по подписке хватает на насыщенный день с отчетами, чатом и распознаваниями.</template></p>
          </div>

          <button
            @click="close"
            class="w-full py-3.5 rounded-xl text-15 font-semibold text-white bg-ink active:bg-[#2a1a10] transition-colors mt-4 shrink-0"
          >Понятно</button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
// Числа берём из бюджета, а не пишем в текст. Раньше здесь стояло зашитое
// «1.000.000 токенов» и выведенные из него «~400 распознаваний» — цифра не
// соответствовала действительности (фактический лимит был 150 000) и
// расходилась бы снова после каждой правки констант в middleware/tokenLimit.js.
import { computed, watch, onUnmounted } from 'vue'
import { useAuthStore } from '../store/auth.js'

const authStore = useAuthStore()

// Средние значения из тех же диапазонов, что названы в тексте: 3 000 на
// распознавание и 4 500 на сообщение с ответом. Недельный отчет — верхняя
// граница 20 000, потому что пугает именно она.
const RECOGNITION = 3000
const MESSAGE = 4500
const WEEKLY_REPORT = 20000

// Та же проверка, что в hasAiAccess и в баннере: флага is_premium мало, он
// остаётся у истёкших подписок. /ai/token-budget про подписку не сообщает.
const isPremium = computed(() => {
  const u = authStore.user
  return !!(u?.is_premium && u.subscription_end && new Date(u.subscription_end) > new Date())
})
const limit = computed(() => Number(authStore.tokenBudget?.limit) || 0)
const fmtLimit = computed(() => limit.value.toLocaleString('ru-RU').replace(/\u00A0|\s/g, '.'))
const recognitions = computed(() => Math.round(limit.value / RECOGNITION))
const messages = computed(() => Math.round(limit.value / MESSAGE))
const weeklyPct = computed(() => limit.value ? Math.round(WEEKLY_REPORT / limit.value * 100) : 0)

const props = defineProps({ modelValue: Boolean })
const emit = defineEmits(['update:modelValue'])

let savedScrollY = 0

watch(() => props.modelValue, (val) => {
  if (val) {
    savedScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.overflow = ''
    window.scrollTo(0, savedScrollY)
  }
})

onUnmounted(() => {
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.overflow = ''
})

function close() {
  emit('update:modelValue', false)
}
</script>

<style scoped>
.token-info-enter-active,
.token-info-leave-active {
  transition: opacity 0.3s ease;
}
.token-info-enter-active .relative,
.token-info-leave-active .relative {
  transition: transform 0.3s ease;
}
.token-info-enter-from,
.token-info-leave-to {
  opacity: 0;
}
.token-info-enter-from .relative {
  transform: translateY(100%);
}
.token-info-leave-to .relative {
  transform: translateY(100%);
}
</style>
